/** @jsxRuntime classic */
/** @jsx jsx */

import React, { useMemo } from 'react';
import { css, jsx, useTheme, SerializedStyles } from '@emotion/react';
import { IAddNewSlotButtonProps, IAddNewSlotButtonsContainerProps } from '../../types';
import { getTimeFromMinutes } from '../../utils/timeUtils';
import { IColors } from '../../../types';
import { useActivityTimeGradients } from './hooks/useActivityTimeGradients';
import { CHARACHERS } from '../../../lib/constants';

const baseCss = (colors: IColors) => css`
	position: absolute;
	border: none;
	&:hover {
		border: 1px solid ${colors.bg_text_primary};
	}
`;

const customCss = (background: string) => css`
	background: ${background};
`;

const horizontalCss = (size: number, index: number) => css`
	width: ${size}px;
	height: 100%;
	left: ${index * size}px;
`;

const verticalCss = (size: number, index: number) => css`
	height: ${size}px;
	width: 100%;
	top: ${index * size + 7}px;
`;

const addSlotButtonCss = (colors: IColors, layoutCss: SerializedStyles, customCss?: SerializedStyles) => css`
	${baseCss(colors)};
	${layoutCss};
	${customCss};
`;

const AddNewSlotButton: React.FC<IAddNewSlotButtonProps> = ({
	index,
	parentId,
	size,
	horizontal,
	duration,
	onClick,
	customCss,
	colors,
	date,
}) => {
	const startTime: string = getTimeFromMinutes(index * duration);
	const endTime: string = getTimeFromMinutes((index + 1) * duration);

	const layoutCss: SerializedStyles = horizontal ? horizontalCss(size, index) : verticalCss(size, index);

	const handleClick = () => {
		const [id] = parentId.split(CHARACHERS.UNDERSCORE);

		onClick?.({
			type: 'NEW_BOOKING',
			data: { parentId: id, startTime, endTime, date },
		});
	};

	return (
		<div
			key={`${parentId}-slot-${index}`}
			onClick={handleClick}
			css={addSlotButtonCss(colors, layoutCss, customCss)}
			data-aid={`${parentId}-add-slot-button-${index}`}
		/>
	);
};

export const AddNewSlotButtonsContainer: React.FC<IAddNewSlotButtonsContainerProps> = ({
	parentId,
	horizontal,
	activityTimes = [],
	onClick,
	hourSize,
	date,
}) => {
	const colors: IColors = useTheme();
	const { gradients, size, duration } = useActivityTimeGradients({ activityTimes, hourSize, colors });

	return (
		<div key={`AddNewSlotButton-Container-${parentId}`} data-aid={`add-slot-buttons-container-${parentId}`}>
			{gradients.map((gradient, idx) => (
				<AddNewSlotButton
					key={`AddNewSlotButton-${parentId}-${idx}`}
					index={idx}
					parentId={parentId}
					size={size}
					duration={duration}
					horizontal={horizontal}
					onClick={onClick}
					customCss={customCss(gradient)}
					colors={colors}
					date={date}
				/>
			))}
		</div>
	);
};
